'use client';

import { useCallback, useRef, useState } from 'react';
import { shouldSubmitOnEnter } from '../../utils/composerKeys';
import { generateClientChatId } from '../../../lib/utils/client-chat-id';
import type { FilePart } from '../../../lib/db/types';
import type { AttachedSkill } from '../../skills/types';

/** Everything the first message carries besides its text. */
export interface StartChatOptions {
  /**
   * Client-minted id for the chat about to be created. Attachments are uploaded
   * under this id BEFORE the chat row exists, and the create call reuses it, so
   * files picked on the start screen land in the chat they belong to.
   */
  chatId: string;
  files?: FilePart[];
  skill?: AttachedSkill | null;
}

export interface StartScreenComposer {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Chat id reserved for the chat this composer will create. */
  pendingChatId: string;
  attachedSkill: AttachedSkill | null;
  attachSkill: (skill: AttachedSkill) => void;
  clearAttachedSkill: () => void;
  handleSubmit: (e: React.FormEvent, files?: FilePart[]) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleTopicClick: (topicPrompt: string) => void;
}

/**
 * Shared composer behaviour for the (uncontrolled) start-screen textareas.
 * Enter submits via the form — but never mid-IME-composition (CJK-safe) — so
 * the compact and full variants can't drift apart.
 *
 * Also holds the pre-chat context the first message needs: a reserved chat id
 * (for uploads), attached files, and an attached skill.
 */
export function useStartScreenComposer(
  onStartChat: (query: string, options: StartChatOptions) => void
): StartScreenComposer {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [pendingChatId, setPendingChatId] = useState<string>(() => generateClientChatId());
  const [attachedSkill, setAttachedSkill] = useState<AttachedSkill | null>(null);

  const attachSkill = useCallback((skill: AttachedSkill) => setAttachedSkill(skill), []);
  const clearAttachedSkill = useCallback(() => setAttachedSkill(null), []);

  const handleSubmit = useCallback(
    (e: React.FormEvent, files?: FilePart[]) => {
      e.preventDefault();
      const query = inputRef.current?.value || '';
      const hasFiles = !!files && files.length > 0;
      if (!query.trim() && !hasFiles) return;

      onStartChat(query, {
        chatId: pendingChatId,
        ...(hasFiles ? { files } : {}),
        ...(attachedSkill ? { skill: attachedSkill } : {}),
      });

      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.style.height = 'auto';
      }
      // The reserved id is now spoken for; reserve a fresh one for the next chat.
      setPendingChatId(generateClientChatId());
      setAttachedSkill(null);
    },
    [attachedSkill, onStartChat, pendingChatId]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSubmitOnEnter(e)) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }, []);

  const handleTopicClick = useCallback((topicPrompt: string) => {
    const el = inputRef.current;
    if (!el) return;
    // Assign through the native setter and dispatch `input` so React's onChange
    // runs: the composer tracks emptiness (Send enablement) and auto-grow from
    // that event. A plain `el.value = …` would leave Send disabled until the
    // user typed a character.
    const nativeSetter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype,
      'value'
    )?.set;
    if (nativeSetter) {
      nativeSetter.call(el, topicPrompt);
    } else {
      el.value = topicPrompt;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.focus();
  }, []);

  return {
    inputRef,
    pendingChatId,
    attachedSkill,
    attachSkill,
    clearAttachedSkill,
    handleSubmit,
    handleKeyDown,
    handleTopicClick,
  };
}
